import { ref, computed, type WritableComputedRef } from 'vue'
import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import { setActiveLocale } from '../utils/activeLocale'

export interface LocaleOption {
  code: string
  name: string
  flag?: string
}

interface I18nInstance {
  locale: WritableComputedRef<string>
}

const DEFAULT_LOCALES: LocaleOption[] = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
]

export const useLocaleStore = defineStore('core-locale', () => {
  const currentLocale = ref<string>('en')
  const availableLocales = ref<LocaleOption[]>(DEFAULT_LOCALES)

  let i18n: I18nInstance | null = null

  try {
    i18n = useI18n({ useScope: 'global' }) as unknown as I18nInstance
  } catch {
    // i18n not available yet
  }

  if (typeof window !== 'undefined') {
    const userSetLocale = localStorage.getItem('app-locale')
    if (userSetLocale) {
      currentLocale.value = userSetLocale
    } else {
      const browserLang = navigator.language.split('-')[0]
      const supportedLocale = availableLocales.value.find((l) => l.code === browserLang)
      if (supportedLocale) {
        currentLocale.value = browserLang
      }
    }
  }

  const currentLocaleName = computed(() => {
    const locale = availableLocales.value.find((l) => l.code === currentLocale.value)
    return locale?.name || currentLocale.value
  })

  const isUsingBrowserLanguage = computed(() => {
    if (typeof window === 'undefined') return false
    return !localStorage.getItem('app-locale')
  })

  function applyLocale() {
    if (i18n) {
      i18n.locale.value = currentLocale.value
    }
    // Keep the pure formatters in step: `formatPrice` and friends read this
    // rather than `useI18n()`, which they cannot call.
    setActiveLocale(currentLocale.value)
  }

  function setLocale(code: string) {
    currentLocale.value = code
    if (typeof window !== 'undefined') {
      localStorage.setItem('app-locale', code)
    }
    applyLocale()
  }

  function isCurrentLocale(code: string): boolean {
    return currentLocale.value === code
  }

  function resetToAutoDetect() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('app-locale')
      const browserLang = navigator.language.split('-')[0]
      const supportedLocale = availableLocales.value.find((l) => l.code === browserLang)
      if (supportedLocale) {
        currentLocale.value = browserLang
      }
      applyLocale()
    }
  }

  function setAvailableLocales(locales: LocaleOption[]) {
    availableLocales.value = locales
  }

  function initLocale() {
    try {
      i18n = useI18n({ useScope: 'global' }) as unknown as I18nInstance
      applyLocale()
    } catch {
      // i18n not available
    }
  }

  function syncFromUserLanguage(language: string) {
    if (!language) return
    const supported = availableLocales.value.find((l) => l.code === language)
    if (supported && currentLocale.value !== language) {
      currentLocale.value = language
      if (typeof window !== 'undefined') {
        localStorage.setItem('app-locale', language)
      }
      applyLocale()
    }
  }

  // Apply locale on initial load if i18n is available
  if (i18n) {
    applyLocale()
  }

  return {
    currentLocale,
    availableLocales,
    currentLocaleName,
    isUsingBrowserLanguage,
    setLocale,
    isCurrentLocale,
    resetToAutoDetect,
    setAvailableLocales,
    initLocale,
    syncFromUserLanguage,
  }
})
